import { UnitSystem } from '@/interfaces/units';
import {
  CM_TO_INCHES,
  INCHES_TO_CM,
  MPA_TO_PSI,
  PSI_TO_MPA,
  roundAfterUnitConversion,
} from '@/lib/unitConversion/unitConversionFactors';
import { DesignScenarioType } from '@/modules/design/interfaces/shared';
import { IAcOverlayDesignImperial, IAcOverlayDesignMetric } from '@/modules/design/interfaces/acOverlay';
import { BaseScenarioType } from '@/lib/types/IBaseScenario';

export const acOverlayImperialToMetric = function (A: IAcOverlayDesignImperial): IAcOverlayDesignMetric {
  const B: IAcOverlayDesignMetric = {
    id: A.id,
    user: A.user,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: UnitSystem.Metric,
    scenarioType: BaseScenarioType.design,
    designScenarioType: DesignScenarioType.acOverlay,
    unitSystem: UnitSystem.Metric,
    isComputedFromLegacy: A.isComputedFromLegacy,

    // IDesignBaseScenario information
    name: A.name,
    description: A.description,
    state: A.state,

    // Pavement Design
    completionYear: A.completionYear, // Years
    roadwayClassification: A.roadwayClassification,
    pavementType: A.pavementType,

    // Layers
    existingLayers: A.existingLayers ? A.existingLayers.map((L) => ({
      id: L.id,
      type: L.type,
      thickness_cm: roundAfterUnitConversion(L.thickness_in * INCHES_TO_CM),
      layerCoefficient: L.layerCoefficient,
      drainageCoefficient: L.drainageCoefficient,
      sn: L.sn,
    })) : [],
    subgradeSoilType: A.subgradeSoilType,
    subgradeRValue: A.subgradeRValue,
    subgradeCbr: A.subgradeCbr,
    modulusCalculationMethod: A.modulusCalculationMethod,

    subgradeModulus_mpa: A.subgradeModulus_psi
      ? roundAfterUnitConversion(A.subgradeModulus_psi * PSI_TO_MPA)
      : A.subgradeModulus_psi,


    existingLayerCoeff: A.existingLayerCoeff,
    overlayLayerMinimumThickness_cm: A.overlayLayerMinimumThickness_in
      ? roundAfterUnitConversion(A.overlayLayerMinimumThickness_in * INCHES_TO_CM) : A.overlayLayerMinimumThickness_in,

    // Structural Evaluation
    structuralEvaluationMethod: A.structuralEvaluationMethod,

    // Condition Survey
    coresTaken: A.coresTaken,
    crackCoresTaken: A.crackCoresTaken,
    topDownCracking: A.topDownCracking,
    stripping: A.stripping,

    alligatorLow: A.alligatorLow,
    alligatorMed: A.alligatorMed,
    alligatorHigh: A.alligatorHigh,

    transverseLow: A.transverseLow,
    transverseMed: A.transverseMed,
    transverseHigh: A.transverseHigh,

    millRemoveDistressed: A.millRemoveDistressed,

    crackDepth_cm: A.crackDepth_in ? roundAfterUnitConversion(A.crackDepth_in * INCHES_TO_CM) : A.crackDepth_in,
    distressDepth_cm: A.distressDepth_in ? roundAfterUnitConversion(A.distressDepth_in * INCHES_TO_CM) : A.distressDepth_in,
    removeDepth_cm: A.removeDepth_in ? roundAfterUnitConversion(A.removeDepth_in * INCHES_TO_CM) : A.removeDepth_in,

    // Nondestructive testing
    designSubgradeModulus_mpa: A.designSubgradeModulus_psi
      ? roundAfterUnitConversion(A.designSubgradeModulus_psi * PSI_TO_MPA) : A.designSubgradeModulus_psi,
    combinedLayerStiffness: A.combinedLayerStiffness,
    estimatedStructuralCoefficient: (A.estimatedStructuralCoefficient ? A.estimatedStructuralCoefficient / 2.54 : A.estimatedStructuralCoefficient),

    // Design Parameters
    designPeriod_years: A.designPeriod_years, // Years
    reliabilityLevel: A.reliabilityLevel, // Unitless, Standard Normal Deviate
    standardError: A.standardError, // Unitless,

    initialServiceability: A.initialServiceability, // Present Servicability Index (p_i)
    terminalServiceability: A.terminalServiceability, // Present Servicability Index at EOL(p_t)
    deltaServiceability: A.deltaServiceability, // TODO: Redundant -> calculated???

    // Traffic and Loading
    esalCalculatedFrom: A.esalCalculatedFrom,

    // From AADT
    initialTraffic: A.initialTraffic, // Completion year traffic
    trafficGrowthRate: A.trafficGrowthRate,
    esalGrowthRate: A.esalGrowthRate,

    // From ESALs
    esals: A.esals,
    loadEquivalencyFactor: A.loadEquivalencyFactor,

    // From Design ESALs
    totalDesignEsals: A.totalDesignEsals,
  };

  return B;
};

export const acOverlayMetricToImperial = function (A: IAcOverlayDesignMetric): IAcOverlayDesignImperial {
  const B: IAcOverlayDesignImperial = {
    id: A.id,
    user: A.user,
    project: A.project,
    created: A.created,
    modified: A.modified,
    visible: A.visible,
    displayUnits: UnitSystem.Imperial,
    scenarioType: BaseScenarioType.design,
    designScenarioType: DesignScenarioType.acOverlay,
    unitSystem: UnitSystem.Imperial,
    isComputedFromLegacy: A.isComputedFromLegacy,

    // IDesignBaseScenario information
    name: A.name,
    description: A.description,
    state: A.state,

    // Pavement Design
    completionYear: A.completionYear, // Years
    roadwayClassification: A.roadwayClassification,
    pavementType: A.pavementType,

    // Layers
    existingLayers: A.existingLayers ? A.existingLayers.map((L) => ({
      id: L.id,
      type: L.type,
      thickness_in: L.thickness_cm * CM_TO_INCHES,
      layerCoefficient: L.layerCoefficient,
      drainageCoefficient: L.drainageCoefficient,
      sn: L.sn,
    })) : [],

    subgradeSoilType: A.subgradeSoilType,
    subgradeRValue: A.subgradeRValue,
    subgradeCbr: A.subgradeCbr,
    modulusCalculationMethod: A.modulusCalculationMethod,

    subgradeModulus_psi: A.subgradeModulus_mpa ? roundAfterUnitConversion(A.subgradeModulus_mpa * MPA_TO_PSI) : A.subgradeModulus_mpa,

    existingLayerCoeff: A.existingLayerCoeff,
    overlayLayerMinimumThickness_in: A.overlayLayerMinimumThickness_cm ? roundAfterUnitConversion(A.overlayLayerMinimumThickness_cm * CM_TO_INCHES) : A.overlayLayerMinimumThickness_cm,

    // Structural Evaluation
    structuralEvaluationMethod: A.structuralEvaluationMethod,

    // Condition Survey
    coresTaken: A.coresTaken,
    crackCoresTaken: A.crackCoresTaken,
    topDownCracking: A.topDownCracking,
    stripping: A.stripping,

    alligatorLow: A.alligatorLow,
    alligatorMed: A.alligatorMed,
    alligatorHigh: A.alligatorHigh,

    transverseLow: A.transverseLow,
    transverseMed: A.transverseMed,
    transverseHigh: A.transverseHigh,

    millRemoveDistressed: A.millRemoveDistressed,

    crackDepth_in: A.crackDepth_cm ? roundAfterUnitConversion(A.crackDepth_cm * CM_TO_INCHES) : A.crackDepth_cm,
    distressDepth_in: A.distressDepth_cm ? roundAfterUnitConversion(A.distressDepth_cm * CM_TO_INCHES) : A.distressDepth_cm,
    removeDepth_in: A.removeDepth_cm ? roundAfterUnitConversion(A.removeDepth_cm * CM_TO_INCHES) : A.removeDepth_cm,

    // Nondestructive Testing
    designSubgradeModulus_psi: A.designSubgradeModulus_mpa ? roundAfterUnitConversion(A.designSubgradeModulus_mpa * MPA_TO_PSI) : A.designSubgradeModulus_mpa,
    combinedLayerStiffness: A.combinedLayerStiffness,
    estimatedStructuralCoefficient: (A.estimatedStructuralCoefficient ? A.estimatedStructuralCoefficient * 2.54 : A.estimatedStructuralCoefficient),


    // Design Parameters
    designPeriod_years: A.designPeriod_years, // Years
    reliabilityLevel: A.reliabilityLevel, // Unitless, Standard Normal Deviate
    standardError: A.standardError, // Unitless,

    initialServiceability: A.initialServiceability, // Present Servicability Index (p_i)
    terminalServiceability: A.terminalServiceability, // Present Servicability Index at EOL(p_t)
    deltaServiceability: A.deltaServiceability, // TODO: Redundant -> calculated???

    // Traffic and Loading
    esalCalculatedFrom: A.esalCalculatedFrom,

    // From AADT
    initialTraffic: A.initialTraffic, // Completion year traffic
    trafficGrowthRate: A.trafficGrowthRate,
    esalGrowthRate: A.esalGrowthRate,

    // From ESALs
    esals: A.esals,
    loadEquivalencyFactor: A.loadEquivalencyFactor,

    // From Design ESALs
    totalDesignEsals: A.totalDesignEsals,
  };

  return B;
};
